import { FC, ReactNode } from 'react';

import { Loading } from '@shared/components/Loading';

//* 관리자 통계 페이지 공용 조각. 모바일 접속이 많아 전부 작은 글씨·좁은 패딩 기준.

export const CenterLoading: FC = () => (
  <div className="center-box min-h-[220px]">
    <Loading />
  </div>
);

export const ErrorBox: FC<{ message: string; onRetry: () => void }> = ({
  message,
  onRetry,
}) => (
  <div className="flex min-h-[220px] items-center justify-center">
    <div className="text-center">
      <p className="text-base font-semibold text-red-600">오류 발생</p>
      <p className="mt-1 text-xs text-gray-500">{message}</p>
      <button
        onClick={onRetry}
        className="mt-3 rounded-lg bg-indigo-600 px-4 py-2 text-xs font-medium text-white hover:bg-indigo-700"
      >
        다시 시도
      </button>
    </div>
  </div>
);

export const EmptyBox: FC<{ message?: string }> = ({ message }) => (
  <div className="rounded-lg bg-white p-8 text-center text-sm text-gray-400 shadow-smd">
    {message ?? '데이터가 없습니다.'}
  </div>
);

export const NoticeBox: FC<{ children: ReactNode; tone?: 'warn' | 'info' }> = ({
  children,
  tone = 'info',
}) => (
  <div
    className={`rounded-lg px-3 py-2 text-13 leading-5 ${
      tone === 'warn' ? 'bg-orange-50 text-orange-800' : 'bg-gray-100 text-gray-600'
    }`}
  >
    {children}
  </div>
);

/** 숫자 KPI 카드 — 모바일 2열, 데스크톱 4열 그리드 안에 놓는다 */
export const StatCard: FC<{
  label: string;
  value: string;
  sub?: string;
  accent?: 'indigo' | 'emerald' | 'red' | 'orange' | 'gray';
}> = ({ label, value, sub, accent }) => {
  const colorMap = {
    indigo: 'text-indigo-700',
    emerald: 'text-emerald-700',
    red: 'text-red-600',
    orange: 'text-orange-600',
    gray: 'text-gray-900',
  };
  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5">
      <p className="text-13 font-medium leading-5 text-gray-600">{label}</p>
      {/* 숫자가 이 카드의 본문 — 한눈에 읽히도록 크게 */}
      <p
        className={`mt-0.5 text-26 font-bold leading-8 tabular-nums ${
          colorMap[accent ?? 'gray']
        }`}
      >
        {value}
      </p>
      {sub && <p className="text-12 leading-4 tabular-nums text-gray-500">{sub}</p>}
    </div>
  );
};

/** 선택형 필터 칩 묶음 */
export function ChipGroup<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: readonly { value: T; label: string }[];
  onChange: (next: T) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="w-11 shrink-0 text-13 font-medium text-gray-600">{label}</span>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          className={`rounded-lg px-3 py-1.5 text-13 font-medium ${
            value === option.value
              ? 'bg-indigo-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/**
 * 평균 대비 지수(1.0 = 기간 평균만큼)의 색.
 * 기준은 답례품 전환 달력(`GiftConversionCalendar`)과 같게 맞춘다 —
 * 0.8 미만이면 경고, 1.1 초과면 호조.
 */
export const baselineIndexTone = (index: number | null): string =>
  index === null
    ? 'text-gray-400'
    : index < 0.8
    ? 'text-red-600'
    : index > 1.1
    ? 'text-emerald-700'
    : 'text-gray-700';

/** 평균 대비 지수 표기 — '95%' / '128%' */
export const formatBaselineIndex = (index: number | null): string =>
  index === null ? '-' : `${Math.round(index * 100)}%`;

export const TH_CLASS =
  'whitespace-nowrap px-2.5 py-2.5 text-right text-12 font-semibold text-gray-600';
export const TD_CLASS =
  'whitespace-nowrap px-2.5 py-2 text-right text-14 tabular-nums text-gray-800';
